import { request } from './client.js'

export type SnapshotState = 'DISUSUN' | 'TERSEGEL' | 'TERDISTRIBUSI' | 'SIAP' | 'DIBATALKAN'

export type SnapshotAction =
  'ubah' | 'segel' | 'distribusi' | 'lepas-kunci' | 'buka-amplop' | 'batal'

export interface SessionRef {
  readonly id: string
  readonly name: string
  readonly date: string
  readonly sessionOrder: number
  readonly status: string
  readonly tuk: { readonly code: string; readonly name: string }
}

export interface Snapshot {
  readonly id: string
  readonly sessionId: string
  readonly session: SessionRef
  readonly state: SnapshotState
  readonly contentHash: string
  readonly payloadObjectKey: string | null
  readonly payloadBytes: number | null
  readonly builtBy: string | null
  readonly sealedBy: string | null
  readonly sealedAt: string | null
  readonly approvedBy: string | null
  readonly distributedAt: string | null
  readonly keyReleasedAt: string | null
  readonly keyReleasedBy: string | null
  readonly offlineOpenedAt: string | null
  readonly offlineOpenedBy: string | null
  readonly offlineOpenedReason: string | null
  readonly cancelledAt: string | null
  readonly cancelledReason: string | null
  readonly createdAt: string
  /**
   * Aksi yang diizinkan mesin keadaan di server.
   *
   * Dibaca apa adanya, tidak diturunkan ulang dari `state` di sini. Dua salinan
   * aturan yang menyimpang berarti tombol yang tampak dapat ditekan padahal
   * server menolaknya — dan itu baru ketahuan saat panitia menekannya.
   */
  readonly aksiTersedia: readonly SnapshotAction[]
}

export interface SnapshotStation {
  readonly rotationNumber: number
  readonly station: {
    readonly sourceCode: string
    readonly title: string
    readonly categoryName: string
    readonly version: number
  }
}

export type ReleaseVerdict =
  | { readonly ok: true }
  | {
      readonly ok: false
      readonly reason: 'TERLALU_AWAL' | 'BELUM_TERDISTRIBUSI'
      readonly message: string
    }

export interface SnapshotDetail extends Snapshot {
  readonly stations: readonly SnapshotStation[]
  /**
   * Jumlah rotasi yang ada tetapi sengaja tidak dikirim.
   *
   * Hanya muncul bagi cakupan non-nasional: panitia dan koordinator di TUK
   * boleh melihat keadaan snapshot sesinya, tetapi tidak kode maupun judul
   * station-nya. Ada dan bernilai berarti `stations` kosong **bukan** karena
   * snapshotnya kosong.
   */
  readonly stationsDisembunyikan?: number
  readonly pelepasanKunci: ReleaseVerdict
}

/**
 * Hasil penyegelan.
 *
 * `frasaAmplopLuring` hanya ada pada respons ini dan tidak pernah dapat dibaca
 * lagi — ia tidak tersimpan di basis data dalam bentuk apa pun.
 */
export interface SealResult extends Snapshot {
  readonly frasaAmplopLuring: string
  readonly peringatan: string
}

export interface SessionRow extends SessionRef {
  readonly tukId: string
  readonly wave: { readonly name: string }
  readonly snapshot: { readonly id: string; readonly state: SnapshotState } | null
}

export async function listSessions(): Promise<readonly SessionRow[]> {
  return request<readonly SessionRow[]>('/api/v1/sesi')
}

export async function list(): Promise<readonly Snapshot[]> {
  return request<readonly Snapshot[]>('/api/v1/snapshot')
}

export async function detail(id: string): Promise<SnapshotDetail> {
  return request<SnapshotDetail>(`/api/v1/snapshot/${id}`)
}

export async function build(
  sessionId: string,
  stationSourceCodes: readonly string[],
): Promise<Snapshot & { jumlahStation: number }> {
  return request('/api/v1/snapshot', {
    method: 'POST',
    body: { sessionId, stationSourceCodes },
  })
}

export async function seal(id: string): Promise<SealResult> {
  return request<SealResult>(`/api/v1/snapshot/${id}/segel`, { method: 'POST' })
}

export async function distribute(id: string): Promise<Snapshot> {
  return request<Snapshot>(`/api/v1/snapshot/${id}/distribusi`, { method: 'POST' })
}

/**
 * Melepas kunci sesi.
 *
 * Server mengembalikan kunci datanya; antarmuka ini **tidak menampilkannya**.
 * Tidak ada manusia yang perlu mengetikkan kunci itu ke mana pun, sedangkan
 * menampilkannya di layar menambah satu jalur kebocoran — tangkapan layar,
 * proyektor ruang rapat, orang yang lewat.
 */
export async function releaseKey(id: string): Promise<{ readonly snapshot: Snapshot }> {
  return request(`/api/v1/snapshot/${id}/lepas-kunci`, { method: 'POST' })
}

export async function openEnvelope(
  id: string,
  frasa: string,
  alasan: string,
): Promise<{ readonly snapshot: Snapshot }> {
  return request(`/api/v1/snapshot/${id}/buka-amplop`, {
    method: 'POST',
    body: { frasa, alasan },
  })
}

export async function cancel(id: string, alasan: string): Promise<Snapshot> {
  return request<Snapshot>(`/api/v1/snapshot/${id}/batal`, { method: 'POST', body: { alasan } })
}
